import React, { createContext, useContext, useState, useEffect } from 'react';
import { signIn, signUp, signOut, getCurrentUser, userPool } from '../services/cognitoService';
import logger from '../utils/logger';
import { getFullUrl } from '../config/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export for React components (hook)
export const useCurrentUser = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within an AuthProvider');
  }
  return context.currentUser;
};

// Export for services (non-hook function) - must be a hook to use useContext
export const useCurrentUserForService = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useCurrentUserForService must be used within an AuthProvider');
  }
  return context.currentUser;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  
  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUser(user);
      } catch (err) {
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkCurrentUser();

      }, []); // Empty dependency array ensures this runs only once on mount

  const isTokenValid = async () => {
    try {
      // Check if userInfo exists (user is logged in)
      const userInfo = localStorage.getItem('userInfo');
     
      if (!userInfo) return false;

      // Verify session with backend 
      
      // Get current idToken from Cognito to send in request body
      const cognitoUser = userPool.getCurrentUser();
      let currentIdToken = null;
      
      if (cognitoUser) {
        currentIdToken = await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
            if (err) {
              reject(err);
              return;
            }
            if (session && session.isValid()) {
              resolve(session.getIdToken().getJwtToken());
            } else {
              reject(new Error('Invalid session'));
            }
          });
        });
      }
      
     
      const response = await fetch(getFullUrl('auth/session'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idToken: currentIdToken
        })
      });

      
      response.headers.forEach((value, name) => {
       });
      
      if (!response.ok) {
        const errorText = await response.text();
        return false;
      }

      const data = await response.json();
   
      
      return data.success;
    } catch (error) {
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      // Check if user is logged in
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) throw new Error('No session found');

      // Get current user from Cognito (this will refresh tokens if needed)
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User session expired');
      }

      // Get fresh tokens from Cognito - getCurrentUser already handles session refresh
      const currentUser = await getCurrentUser();
      let refreshedIdToken = null;
      
      if (currentUser) {
        // The user object from getCurrentUser should have session info
        // We need to get a fresh session from Cognito
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
          refreshedIdToken = await new Promise((resolve, reject) => {
            cognitoUser.getSession((err, session) => {
              if (err) {
                reject(err);
                return;
              }
              if (session && session.isValid()) {
                resolve(session.getIdToken().getJwtToken());
              } else {
                reject(new Error('Invalid session'));
              }
            });
          });
        }
      }

      // Call backend to update session with refreshed tokens
      const headers = { 'Content-Type': 'application/json' };
      
      // Fallback: Send token in Authorization header as key=value pair
      // if (refreshedIdToken) {
      //   headers['Authorization'] = `idToken=${refreshedIdToken}`;
      // }
     
      const sessionResponse = await fetch(getFullUrl('auth/session'), {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          idToken: refreshedIdToken
        })
      });

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        throw new Error('Session refresh failed');
      }
     
      // Update current user state
      setCurrentUser(user);

      return true;
    } catch (error) {
      // Clear invalid session
      setCurrentUser(null);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const authResult = await signIn(email, password);
      const user = await getCurrentUser();

      // Get tokens from Cognito
      const idToken = authResult.getIdToken().getJwtToken();
      const accessToken = authResult.getAccessToken().getJwtToken();
      const refreshToken = authResult.getRefreshToken().getToken();
      
      // Call backend to set httpOnly session cookie
      const sessionResponse = await fetch(getFullUrl('auth/session'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken,
          accessToken,
          refreshToken
        })
      });

     
      sessionResponse.headers.forEach((value, name) => {
      });
      
      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        throw new Error('Failed to establish session');
      }
    
      // Store only minimal user info in localStorage (not tokens)
      localStorage.setItem('userInfo', JSON.stringify({
        name: user.name,
        email: user.email,
        id: user.id
      }));

     
      setCurrentUser(user);
      return user;
    } catch (err) {
      throw err;
    }
  };

  const register = async (email, password, name, mobile) => {
    try {
      const result = await signUp(email, password, name, mobile);
      return result;
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    // Call backend to clear session cookie
    try {
      await fetch(getFullUrl('auth/logout'), {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
    }

    // Sign out from Cognito
    signOut();

    // Clear localStorage
    localStorage.removeItem('userInfo');
    localStorage.removeItem('currentUserSession'); // Legacy cleanup

    setCurrentUser(null);
  };

  const value = {
    currentUser,
    user: currentUser, // Add user alias for compatibility
    login,
    register,
    logout,
    refreshToken,
    isTokenValid,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
